import React, { useState } from 'react';
import { useAuth, auth } from './auth-context';
import { useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Check,
    Zap,
    Crown,
    Shield,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

const features = [
    'Unlimited practice questions',
    'All 6 practice modes',
    '90-minute exam simulations',
    'Spaced repetition (FSRS)',
    'Domain-specific drills',
    'Missed questions review',
    'Adaptive difficulty',
    'Progress analytics',
];

const FAQ = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b border-white/10 last:border-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between py-4 text-left"
            >
                <span className="text-sm font-medium text-white/85">{question}</span>
                {isOpen
                    ? <ChevronUp className="h-4 w-4 text-white/45 flex-shrink-0 ml-2" />
                    : <ChevronDown className="h-4 w-4 text-white/45 flex-shrink-0 ml-2" />
                }
            </button>
            {isOpen && (
                <p className="text-sm text-white/55 pb-4 -mt-1">{answer}</p>
            )}
        </div>
    );
};

const Pricing = () => {
    const navigate = useNavigate();
    const { user, isPaidUser } = useAuth();
    const [loading, setLoading] = useState(null); // 'monthly' | 'lifetime' | null
    const [error, setError] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);

    const handleSubscribe = async (plan) => {
        if (!user) {
            navigate('/');
            return;
        }

        if (isPaidUser) return;

        setLoading(plan);
        setError(null);

        try {
            const idToken = await auth.currentUser.getIdToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'https://us-central1-security-plus-crammer.cloudfunctions.net'}/createCheckoutSession`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Firebase-Token': idToken,
                    },
                    body: JSON.stringify({ userId: user.uid, plan }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Checkout error:', response.status, errorData);
                throw new Error('Failed to create checkout session.');
            }

            const { sessionUrl } = await response.json();

            // Validate the redirect URL is a legitimate Stripe checkout URL
            try {
                const url = new URL(sessionUrl);
                if (!url.hostname.endsWith('.stripe.com')) {
                    throw new Error('Invalid checkout URL');
                }
            } catch {
                throw new Error('Invalid checkout URL received.');
            }

            window.location.href = sessionUrl;
        } catch (err) {
            console.error('Subscription error:', err);
            setError('Something went wrong. Please try again.');
            setLoading(null);
        }
    };

    const handleManageSubscription = async () => {
        if (!user) return;
        setPortalLoading(true);
        setError(null);

        try {
            const idToken = await auth.currentUser.getIdToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'https://us-central1-security-plus-crammer.cloudfunctions.net'}/createPortalSession`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Firebase-Token': idToken,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to open subscription management.');
            }

            const { portalUrl } = await response.json();

            // Validate the redirect URL is a legitimate Stripe URL
            try {
                const url = new URL(portalUrl);
                if (!url.hostname.endsWith('.stripe.com')) {
                    throw new Error('Invalid portal URL');
                }
            } catch {
                throw new Error('Invalid portal URL received.');
            }

            window.location.href = portalUrl;
        } catch (err) {
            console.error('Portal error:', err);
            setError('Unable to open subscription management. Please try again.');
            setPortalLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#070B14] text-white">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute top-32 right-[-220px] h-[520px] w-[520px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 container mx-auto px-4 max-w-5xl py-8">
                {/* Back button */}
                <Link
                    to="/"
                    className="inline-flex items-center text-white/55 hover:text-white transition-colors mb-8"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                </Link>

                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                        Pass the Security+ Exam
                    </h1>
                    <p className="text-white/55 max-w-lg mx-auto">
                        Unlock all practice modes and 200+ questions with adaptive learning that targets your weak areas.
                    </p>
                </div>

                {/* Already paid */}
                {isPaidUser && (
                    <div className="max-w-md mx-auto rounded-2xl bg-green-900/20 ring-1 ring-green-500/30 p-6 text-center mb-8">
                        <Check className="h-8 w-8 text-green-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-white mb-1">You have full access!</h3>
                        <p className="text-sm text-white/55 mb-4">All practice modes and features are unlocked.</p>
                        <div className="flex items-center justify-center gap-4">
                            <Link to="/practice" className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                                Go to Practice &rarr;
                            </Link>
                            <span className="text-white/20">|</span>
                            <button
                                onClick={handleManageSubscription}
                                disabled={portalLoading}
                                className="text-white/45 hover:text-white/70 text-sm font-medium transition-colors"
                            >
                                {portalLoading ? 'Opening...' : 'Manage Subscription'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Pricing cards */}
                {!isPaidUser && (
                    <>
                        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
                            {/* Monthly */}
                            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 flex flex-col">
                                <div className="mb-6 mt-2">
                                    <h3 className="text-lg font-semibold text-white mb-1">Monthly</h3>
                                    <p className="text-sm text-white/55">Cancel anytime</p>
                                </div>

                                <div className="mb-6">
                                    <div className="flex items-baseline">
                                        <span className="text-4xl font-bold text-white">$7.99</span>
                                        <span className="text-white/55 ml-1">/month</span>
                                    </div>
                                </div>

                                <ul className="space-y-3 mb-8 flex-grow">
                                    {features.map((feature) => (
                                        <li key={feature} className="flex items-start">
                                            <Check className="h-4 w-4 text-blue-400 mr-2.5 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-white/70">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    onClick={() => handleSubscribe('monthly')}
                                    disabled={loading !== null}
                                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors ring-1 ring-blue-500/50 text-blue-400 hover:bg-blue-500/10
                                        ${loading === 'monthly' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loading === 'monthly' ? 'Redirecting...' : 'Start Monthly'}
                                </button>
                            </div>

                            {/* Lifetime */}
                            <div className="relative rounded-2xl bg-white/5 ring-2 ring-cyan-500/40 p-6 flex flex-col shadow-[0_0_0_1px_rgba(34,211,238,0.15),0_20px_80px_rgba(34,211,238,0.06)]">
                                {/* Best value badge */}
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                    <span className="inline-flex items-center bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                                        <Crown className="h-3 w-3 mr-1" />
                                        BEST VALUE
                                    </span>
                                </div>

                                <div className="mb-6 mt-2">
                                    <h3 className="text-lg font-semibold text-white mb-1">Lifetime</h3>
                                    <p className="text-sm text-white/55">One-time payment, forever access</p>
                                </div>

                                <div className="mb-6">
                                    <div className="flex items-baseline">
                                        <span className="text-4xl font-bold text-white">$29.99</span>
                                        <span className="text-white/55 ml-1">once</span>
                                    </div>
                                    <p className="text-xs text-cyan-400 mt-1">
                                        Pays for itself in under 4 months
                                    </p>
                                </div>

                                <ul className="space-y-3 mb-8 flex-grow">
                                    {features.map((feature) => (
                                        <li key={feature} className="flex items-start">
                                            <Check className="h-4 w-4 text-cyan-400 mr-2.5 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-white/70">{feature}</span>
                                        </li>
                                    ))}
                                    <li className="flex items-start">
                                        <Check className="h-4 w-4 text-cyan-400 mr-2.5 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-white font-medium">All future questions & updates</span>
                                    </li>
                                </ul>

                                <button
                                    onClick={() => handleSubscribe('lifetime')}
                                    disabled={loading !== null}
                                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white
                                        ${loading === 'lifetime' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loading === 'lifetime' ? 'Redirecting...' : 'Get Lifetime Access'}
                                </button>
                            </div>
                        </div>

                        {/* Social proof / note */}
                        <div className="text-center mb-12">
                            <p className="text-sm text-white/45">
                                Most students pass the Security+ within 2-3 months of focused study.
                            </p>
                            <div className="flex items-center justify-center mt-3 space-x-4 text-xs text-white/45">
                                <span className="flex items-center">
                                    <Shield className="h-3.5 w-3.5 mr-1" />
                                    Secure checkout via Stripe
                                </span>
                                <span>|</span>
                                <span>SY0-701 aligned</span>
                            </div>
                        </div>
                    </>
                )}

                {/* Error */}
                {error && (
                    <div className="max-w-md mx-auto bg-red-900/20 ring-1 ring-red-500/30 rounded-xl p-4 text-center mb-8">
                        <p className="text-sm text-red-400">{error}</p>
                    </div>
                )}

                {/* FAQ */}
                <div className="max-w-2xl mx-auto">
                    <h2 className="text-lg font-semibold text-white mb-4">Frequently Asked Questions</h2>
                    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-5">
                        <FAQ
                            question="What happens after I pay?"
                            answer="You'll get instant access to all practice modes, unlimited questions, exam simulations, spaced repetition reviews, and domain-specific drills. Your progress from the free tier carries over."
                        />
                        <FAQ
                            question="Can I cancel the monthly plan?"
                            answer="Yes, you can cancel your monthly subscription at any time from this page. Just click 'Manage Subscription' above to open your billing portal. You'll keep access until the end of your current billing period."
                        />
                        <FAQ
                            question="What's the difference between Monthly and Lifetime?"
                            answer="Both plans unlock the exact same features. Monthly is $7.99/mo and you can cancel anytime. Lifetime is a one-time $29.99 payment that gives you permanent access, including all future questions and updates."
                        />
                        <FAQ
                            question="Is this aligned with the current Security+ exam?"
                            answer="Yes, all questions are written for the CompTIA Security+ SY0-701 exam objectives across all 5 domains."
                        />
                        <FAQ
                            question="Do I need to create an account?"
                            answer="Yes, you'll need to sign in with Google to track your progress and manage your subscription. Your purchase is tied to your account."
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Pricing;

